import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "components/custom.css";
import useTextToSpeech from 'src/utils/useTextToSpeech';
import WordDefinitionModal from "components/PracticeMaterial/PopUps/WordDefinitionModal";

const MultipleAnswer = (props) => {
  // State variables
  const [selectedValue, setSelectedValue] = useState([]);
  const [questionId, setQuestionId] = useState("");
  const [reset, setReset] = useState(false);
  const [secondCount, setSecondCount] = useState(1200);
  const [selectedWord, setSelectedWord] = useState("");
  const [wordDefinitionModalIsOpen, setWordDefinitionModalIsOpen] = useState(false);
  const { speak } = useTextToSpeech({
      language: "English",
      preferMaleVoice: true,
  });  
  // Removed unused states: question, is_answer, correctFlag, resultObj
  // const [question, setQuestion] = useState(""); // Unused - can be derived from props
  // const [is_answer, setIs_answer] = useState(false); // Unused
  // const [correctFlag, setCorrectFlag] = useState(true); // Logic moved to useEffect
  // const [resultObj, setResultObj] = useState({}); // Can be calculated on demand
  
  // Refs
  const intervalIdRef = useRef(null);
  const correctAnswersRef = useRef([]);
  
  // Memoized values
  const questionText = useMemo(() => props.data?.question || "", [props.data]);
  const data = props.data;
  const { testMode, resultMode, answer, languages } = props;

  // Calculate correct answers once when data changes
  useEffect(() => {
    if (data?.option) {
      correctAnswersRef.current = data.option
        .filter(item => item.correct == 1)
        .map(item => item.id);
      
      // Submit correct answers to parent when data changes
      props.submit({ 'correct': correctAnswersRef.current, answer: [] });
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle question changes and resets
  useEffect(() => {
    if (!data) return;
    
    const isNewQuestion = questionId && questionId !== data.id;
    const shouldReset = isNewQuestion || props.restart;
    
    if (shouldReset) {
      // Clear existing interval
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      
      // Reset state
      setSecondCount(1200);
      setQuestionId(data.id);
      setReset(true);
      setSelectedValue([]);
      
      // If restart prop is true, call restartQue
      if (props.restart) {
        props.restartQue();
      }
    } else if (!questionId) {
      setQuestionId(data.id);
    }
  }, [data, props.restart, questionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle timer setup when reset is triggered
  useEffect(() => {
    if (reset) {
      intervalIdRef.current = setInterval(() => {
        setSecondCount(prev => {
          if (prev <= 1) {
            if (intervalIdRef.current) {
              clearInterval(intervalIdRef.current);
              intervalIdRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setReset(false);
    }
  }, [reset]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);
  
  const selectSpan = (e, item) => {
		if (!testMode) {
			// const speech = new Speech();
			// speech.speak({ text: item });
			// speak(item);

			openCloseWordDefinitionModal(true);
			setSelectedWord(item);
		}
	}

  const openCloseWordDefinitionModal = (status) => {
		setWordDefinitionModalIsOpen(status);
        
    if(status == false) setSelectedWord('');
	}  

  // Handle stop recording - commented out as per original
  // useEffect(() => {
  //   if (props.stopRecording) {
  //     stopTest();
  //   }
  // }, [props.stopRecording]);

  // Optimized handler for checkbox changes
  const handleChange = useCallback((event) => {
    if (props.showResult) {
      props.showResult();
    }
    
    const val = parseInt(event.target.value);
    setSelectedValue(prev => {
      let newSelection;
      if (prev.includes(val)) {
        // Remove if already selected
        newSelection = prev.filter(id => id !== val);
      } else {
        // Add if not selected
        newSelection = [...prev, val];
      }
      
      // Submit to parent
      if (props.submit) {
        props.submit({ 
          'answer': newSelection, 
          'correct': correctAnswersRef.current 
        }, true);
      }
      
      return newSelection;
    });
  }, [props.submit, props.showResult]);

  // Optimized class name calculation
  const getOptionClassName = useCallback((item) => {
    if (!answer) return "";
    
    const isSelected = selectedValue.includes(item.id);
    const isCorrect = item.correct === 1;
    
    if (isCorrect) {
      return isSelected ? "success-wrapper" : "normal-wrapper";
    } else {
      return isSelected ? "error-wrapper" : "";
    }
  }, [selectedValue, answer]);

  // Memoized options list - reversed as in original
  const optionsList = useMemo(() => {
    if (!data?.option || resultMode) return null;
    
    return [...data.option].reverse().map((item, i) => (
      <label 
        key={`${item.id}-${i}`} 
        className={`checkbox-wrapper ${getOptionClassName(item)}`}
      >
        <p>{item.options}</p>
        <input
          type="checkbox"
          name="answer"
          value={item.id}
          checked={selectedValue.includes(item.id)}
          onChange={handleChange}
          disabled={!!answer}
          aria-label={`Option: ${item.options}`}
        />
        <span className="checkmark"></span>
      </label>
    ));
  }, [data, resultMode, selectedValue, answer, handleChange, getOptionClassName]);

  // Stop test function - kept for compatibility
  // const stopTest = useCallback(() => {
  //   if (props.nextQuestion) {
  //     const resultObj = { 
  //       'answer': selectedValue, 
  //       'correct': correctAnswersRef.current 
  //     };
  //     props.nextQuestion(resultObj);
  //   }
  // }, [selectedValue, props.nextQuestion]);

  // Show answer function - commented out as unused in current logic
  // const showAnswer = useCallback(() => {
  //   if (props.answer != is_answer) {
  //     setIs_answer(true);
  //   }
  // }, [props.answer, is_answer]);

  return (
    <div className="visibility-visible">
      <div className="custom-box mx-0">
        {/* <p className="gray-text question-text">{questionText}</p> */}
        <p className="gray-text question-text">
          {questionText.split(' ').map(function (item, index) {
            return <React.Fragment key={index}><span className={(testMode) ? "" : "speach-text"} onClick={(e) => selectSpan(e, item)}>{item}</span>&nbsp; </React.Fragment>;
          }, this)}
        </p>        
      </div>

      <div className="radiolist-content custom-box mx-0">
        <div className="checklist-content">
          <h4>{data?.question_mcq}</h4>
          <div className="radiolist-group">
            {optionsList}
          </div>
        </div>
      </div>
      {!testMode && !resultMode &&
        <WordDefinitionModal
          isOpen={wordDefinitionModalIsOpen}
          toggle={openCloseWordDefinitionModal}
          word={selectedWord}
          languages={languages}
        />
      }
    </div>
  );
};

export default MultipleAnswer;